import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, List } from 'lucide-react';
import { toast } from 'sonner';
import Swal from 'sweetalert2';

import API from '../api_config';
import { useAuth } from '../contexts/AuthContext';

export default function UnitsPage() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    symbol: ''
  });

  // SuperAdmin Logic
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const fetchCompanies = useCallback(async () => {
    if (user?.role === 'superadmin') {
      try {
        const response = await axios.get(`${API}/companies`);
        setCompanies(response.data);
      } catch (error) {
        console.error("Error fetching companies", error);
      }
    }
  }, [user]);

  const fetchUnits = useCallback(async () => {
    try {
        let url = `${API}/units`;
         if (user?.role === 'superadmin' && selectedCompany) {
            url += `?target_company_id=${selectedCompany}`;
        }
      const response = await axios.get(url);
      setUnits(response.data);
    } catch (error) {
      toast.error('Error al cargar unidades');
    } finally {
      setLoading(false);
    }
  }, [user, selectedCompany]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update
        let url = `${API}/units/${editingId}`; 
        if (user?.role === 'superadmin' && selectedCompany) {
            url += `?target_company_id=${selectedCompany}`;
        }
        await axios.put(url, formData);
        toast.success('Unidad actualizada');
      } else {
        // Create
        let url = `${API}/units`;
        if (user?.role === 'superadmin' && selectedCompany) {
            url += `?target_company_id=${selectedCompany}`;
        }
        await axios.post(url, formData);
        toast.success('Unidad creada');
      }
      setIsFormOpen(false);
      setEditingId(null);
      setFormData({ name: '', symbol: '' });
      fetchUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar unidad');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "No podrás revertir esto",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        let url = `${API}/units/${id}`;
        if (user?.role === 'superadmin' && selectedCompany) {
            url += `?target_company_id=${selectedCompany}`;
        }
        await axios.delete(url);
        Swal.fire('¡Eliminado!', 'La unidad ha sido eliminada.', 'success');
        fetchUnits();
      } catch (error) {
        Swal.fire('Error', 'No se pudo eliminar la unidad.', 'error');
      }
    }
  };

  const openEditModal = (unit) => {
    setEditingId(unit.id);
    setFormData({ name: unit.name, symbol: unit.symbol });
    setIsFormOpen(true);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({ name: '', symbol: '' });
    setIsFormOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Unidades</h1>
          <p className="text-muted-foreground">Gestión de unidades de medida (kg, lt, un, etc)</p>
        </div>
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Unidad
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Unidad' : 'Nueva Unidad'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Kilogramo"
                  required
                />
              </div>
               <div className="space-y-2">
                <Label htmlFor="symbol">Símbolo</Label>
                <Input
                  id="symbol"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                  placeholder="Ej: kg"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                {editingId ? 'Actualizar' : 'Crear'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

       {user?.role === 'superadmin' && (
          <div className="w-full md:w-1/3 my-4">
              <Label>Filtrar por Empresa (Super Admin)</Label>
              <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
              >
                  <option value="">Mi Empresa (Default)</option>
                  {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
          </div>
      )}

      {loading ? (
        <div>Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {units.map((unit) => (
            <Card key={unit.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {unit.name} ({unit.symbol})
                </CardTitle>
                <List className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(unit)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(unit.id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
           {units.length === 0 && (
              <p className="text-muted-foreground col-span-full text-center py-10">No hay unidades registradas.</p>
          )}
        </div>
      )}
    </div>
  );
}
